/**
 * Shopify Min Quantity + Min Order Value Plugin By Fasc Ai Ventures Pvt. Ltd.
 * Unified plugin for enforcing minimum quantity and minimum order value rules
 * 
 * Features:
 * - Product Page: Enforces minimum quantity before adding to cart
 * - Cart Page: Validates minimum quantity requirements
 */

(function() {
  'use strict';

  // Prevent duplicate initialization if the script tag is included multiple times (e.g., inside quickview)
  if (window.__MinQuantityOrderValuePluginLoaded) {
    return;
  }
  window.__MinQuantityOrderValuePluginLoaded = true;

  // Global flag to track if validation is ready
  window.__MinQuantityValidationReady = false;

  // Early interception: Block all form submissions until validation is ready
  document.addEventListener('submit', function(e) {
    const form = e.target;
    if (form && form.action && (form.action.includes('/cart/add') || form.action.includes('/cart'))) {
      // Check if validation is ready (either global flag or form-specific flag)
      const isFormReady = form.dataset.validationReady === 'true';
      if (!window.__MinQuantityValidationReady && !isFormReady) {
        // Block submission temporarily
        e.preventDefault();
        e.stopPropagation();
        
        // Wait a bit and retry (validation should be ready by then)
        setTimeout(() => {
          const isReadyNow = window.__MinQuantityValidationReady || form.dataset.validationReady === 'true';
          if (isReadyNow) {
            // Re-trigger submit after validation is ready
            const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
            form.dispatchEvent(submitEvent);
          } else {
            // Still not ready after delay, allow (fallback to prevent infinite blocking)
            form.submit();
          }
        }, 200);
        
        return false;
      }
    }
  }, true); // Use capture phase to intercept early

  // Early interception: Block all button clicks until validation is ready
  document.addEventListener('click', function(e) {
    const target = e.target;
    const button = target.closest('button[name="add"], button[name="checkout"], .cart-checkout-btn, .checkout-btn, .shopify-payment-button button, .shopify-payment-button div[role="button"]');
    
    if (button) {
      const form = button.closest('form');
      if (form && form.action && (form.action.includes('/cart/add') || form.action.includes('/cart'))) {
        // Check if validation is ready (either global flag or form-specific flag)
        const isFormReady = form.dataset.validationReady === 'true';
        if (!window.__MinQuantityValidationReady && !isFormReady) {
          // Block click temporarily
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          
          // Wait a bit and retry
          setTimeout(() => {
            const isReadyNow = window.__MinQuantityValidationReady || form.dataset.validationReady === 'true';
            if (isReadyNow) {
              // Re-trigger click after validation is ready
              button.click();
            } else {
              // Still not ready after delay, allow (fallback)
              button.click();
            }
          }, 200);
          
          return false;
        }
      }
    }
  }, true); // Use capture phase to intercept early

  /**
   * Main Plugin Class
   */
  window.MinQuantityOrderValuePlugin = class MinQuantityOrderValuePlugin {
    constructor() {
      this.init();
    }

    init() {
      // Check if we're on product page
      if (this.isProductPage()) {
        this.initProductPage();
      }

      // Check if we're on cart page
      if (this.isCartPage()) {
        this.initCartPage();
      }
    }

    /**
     * Check if current page is product page
     */
    isProductPage() {
      return document.querySelector('form[action*="/cart/add"]') !== null ||
             document.querySelector('#AddToCart') !== null ||
             document.querySelector('button[name="add"]') !== null;
    }

    /**
     * Check if current page is cart page
     */
    isCartPage() {
      return document.querySelector('form[action*="/cart"]') !== null ||
             window.location.pathname.includes('/cart');
    }

    /**
     * Initialize product page functionality
     */
    initProductPage() {
      // Skip quickview initialization on page load - it will be handled by mfpOpen event
      // This prevents interference with Magnific Popup on collection pages
      
      // Only handle regular product pages (not in quickview)
      // Only if it's actually a product page URL
      const isProductPageURL = window.location.pathname.includes('/products/');
      if (isProductPageURL) {
        const regularProductForm = document.querySelector('form[action*="/cart/add"]');
        if (regularProductForm && 
            !regularProductForm.closest('[id^="product-quickview-"]') && 
            !regularProductForm.closest('.mfp-content')) {
          this.initRegularProductPage();
        }
      }
      
      // Quickview modals will be initialized via mfpOpen event handler below
    }

    /**
     * Initialize quickview modal - scoped to only the form section
     */
    initQuickviewModal(container) {
      // Only initialize if container is actually visible in Magnific Popup
      const mfpWrap = document.querySelector('.mfp-wrap.mfp-ready');
      const mfpContent = container.closest('.mfp-content');
      const isVisible = container.offsetParent !== null && 
                       mfpContent !== null &&
                       mfpWrap !== null;
      
      if (!isVisible) {
        return; // Don't initialize hidden modals
      }
      
      // Additional check: ensure we're not on a collection page with hidden quickview HTML
      // Only initialize if the container is actually inside the open Magnific Popup
      if (!mfpContent || !mfpWrap.contains(mfpContent)) {
        return; // Container is not in an open modal
      }

      // Extract product ID from container ID (e.g., "product-quickview-123" -> "123")
      const productIdMatch = container.id.match(/product-quickview-(\d+)/);
      const productId = productIdMatch ? productIdMatch[1] : null;

      if (!productId) {
        return; // Need product ID to find elements
      }

      // Find the form within the quickview container
      const form = container.querySelector('form[action*="/cart/add"]');
      if (!form) {
        return; // Form not found yet, will retry
      }

      // Find elements specifically within the form (the section you showed)
      const addToCartBtn = form.querySelector(`#AddToCart-${productId}`) || 
                          form.querySelector('.AddToCartQV') ||
                          form.querySelector('button[name="add"]');
      
      const quantityInput = form.querySelector(`#Quantity-${productId}`) || 
                          form.querySelector('input[name="quantity"]');

      if (!addToCartBtn || !quantityInput) {
        return; // Elements not ready yet
      }

      // Get product data scoped to this container
      const productData = this.getProductData(container);

      if (!productData) {
        return; // Data not injected yet
      }

      // Check if already initialized on this form
      if (form.dataset.minOrderInitialized === 'true') {
        return;
      }
      form.dataset.minOrderInitialized = 'true';
      
      // Mark this form as validation-ready
      form.dataset.validationReady = 'true';
      
      // Mark this form as validation-ready
      form.dataset.validationReady = 'true';

      // Store plugin instance reference for use in callbacks
      const pluginInstance = this;

      // Validation function - scoped to this quickview only
      const validateQuickview = (e) => {
        // Only validate if the event target is within the form or is the button
        const target = e.target;
        const isFormRelated = form.contains(target) || 
                            target === form || 
                            target === addToCartBtn ||
                            addToCartBtn.contains(target);
        
        if (!isFormRelated) {
          return true; // Don't interfere with clicks outside the form
        }

        // Don't validate on non-submit/click events
        if (e && e.type && e.type !== 'submit' && e.type !== 'click') {
          return true;
        }

        const qty = parseInt(quantityInput.value, 10) || 1;

        // Validate minimum quantity
        if (productData.enableMinQuantity && qty < productData.minQuantity) {
          if (e) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
          }
          pluginInstance.showAlert(
            `A minimum order quantity of ${productData.minQuantity} units is required for this item.`,
            'error'
          );
          quantityInput.focus();
          quantityInput.select();
          return false;
        }


        return true;
      };

      // Handle form submission - use bubble phase to avoid interfering with Magnific Popup
      form.addEventListener('submit', function(e) {
        // Only validate on actual form submission, not on other events
        if (e.type === 'submit') {
          if (!validateQuickview(e)) {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }
        }
      }, false);

      // Handle button click - only validate on actual button click, don't interfere with other clicks
      addToCartBtn.addEventListener('click', function(e) {
        // Only validate if click is directly on button or its children
        // Don't prevent default or stop propagation unless validation fails
        if (addToCartBtn.contains(e.target) || e.target === addToCartBtn) {
          // Validate but don't interfere with the click event unless validation fails
          const isValid = validateQuickview(e);
          if (!isValid) {
            // Only stop propagation if validation failed
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            return false;
          }
          // If validation passes, let the form submit normally
        }
        // Let all other clicks bubble normally to Magnific Popup
      }, false); // Use bubble phase to avoid interfering with Magnific Popup

      // Don't interfere with quantity +/- buttons - let them work normally
      // Validation only happens on add to cart

      // Handle Shopify Payment Button (Buy Now button) for quickview
      this.initPaymentButtonValidation(form, productData, quantityInput);
    }

    /**
     * Initialize regular product page (not quickview)
     */
    initRegularProductPage() {
      const addToCartBtn = document.querySelector('#AddToCart') || 
                          document.querySelector('button[name="add"]') ||
                          document.querySelector('form[action*="/cart/add"] button[type="submit"]');
      
      const quantityInput = document.querySelector('input[name="quantity"]') ||
                           document.querySelector('#Quantity') ||
                           document.querySelector('.quantity-input input');

      if (!addToCartBtn || !quantityInput) {
        return;
      }

      // Get product data
      const productData = this.getProductData(document);

      if (!productData) {
        return;
      }

      // Get the form
      const form = addToCartBtn.closest('form');
      if (!form) return;

      // Check if already initialized
      if (form.dataset.minOrderInitialized === 'true') {
        return;
      }
      form.dataset.minOrderInitialized = 'true';

      // Handle form submission
      if (form) {
        form.addEventListener('submit', (e) => {
          if (!this.validateProductPage(productData, quantityInput, e)) {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }
        });
      }

      // Handle button click
      addToCartBtn.addEventListener('click', (e) => {
        if (!this.validateProductPage(productData, quantityInput, e)) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      });

      // Handle Shopify Payment Button (Buy Now button)
      this.initPaymentButtonValidation(form, productData, quantityInput);
    }

    /**
     * Initialize payment button (Buy Now) validation - checks minimum order value of ₹5,000
     */
    initPaymentButtonValidation(form, productData, quantityInput) {
      if (!form || !productData) {
        return;
      }

      // Store plugin instance reference for use in callbacks
      const pluginInstance = this;

      // Function to validate and attach handlers to payment button
      let retryCount = 0;
      const maxRetries = 10; // Try for up to 5 seconds (10 * 500ms)
      
      const attachPaymentButtonHandlers = () => {
        const paymentButtonContainer = form.querySelector('.shopify-payment-button');
        if (!paymentButtonContainer) {
          // Payment button not loaded yet, retry
          retryCount++;
          if (retryCount < maxRetries) {
            setTimeout(attachPaymentButtonHandlers, 500);
          }
          return;
        }

        // Check if already initialized
        if (paymentButtonContainer.dataset.minOrderValueHandlerAdded === 'true') {
          return;
        }
        paymentButtonContainer.dataset.minOrderValueHandlerAdded = 'true';

        // Find the actual payment button (can be branded or unbranded)
        const paymentButton = paymentButtonContainer.querySelector('button, div[role="button"], .shopify-payment-button__button');
        
        if (paymentButton) {
          // Function to get current product price - handles both regular pages and quickview
          const getCurrentProductPrice = () => {
            let productPrice = 0;
            
            // Method 1: Check if we're in a quickview modal - get from ProductQuickViewJson
            const quickviewContainer = form.closest('[id^="product-quickview-"]');
            if (quickviewContainer) {
              const productIdMatch = quickviewContainer.id.match(/product-quickview-(\d+)/);
              const productId = productIdMatch ? productIdMatch[1] : null;
              
              if (productId) {
                // Try to get product JSON from quickview
                const productJsonEl = document.getElementById(`ProductQuickViewJson-${productId}`);
                if (productJsonEl) {
                  try {
                    const productJson = JSON.parse(productJsonEl.textContent);
                    const variantSelect = form.querySelector('select[name="id"]');
                    if (variantSelect && productJson.variants) {
                      const variantId = parseInt(variantSelect.value);
                      const variant = productJson.variants.find(v => v.id === variantId);
                      if (variant && variant.price) {
                        productPrice = parseFloat(variant.price) / 100; // Convert from cents to rupees
                      }
                    }
                  } catch (e) {
                    console.error('Error parsing quickview product JSON:', e);
                  }
                }
              }
            }
            
            // Method 2: Try to get from productData (from Liquid injection) - use current variant
            if (!productPrice || productPrice === 0) {
              const variantSelect = form.querySelector('select[name="id"], input[name="id"]:checked');
              if (variantSelect && productData) {
                const variantId = parseInt(variantSelect.value);
                
                // Try window.productJson (for regular product pages)
                if (window.productJson && window.productJson.variants) {
                  const variant = window.productJson.variants.find(v => v.id === variantId);
                  if (variant && variant.price) {
                    productPrice = parseFloat(variant.price) / 100; // Convert from cents to rupees
                  }
                }
                
                // Fallback to productData.price if no variant found
                if ((!productPrice || productPrice === 0) && productData.price) {
                  productPrice = parseFloat(productData.price);
                }
              } else if (productData && productData.price) {
                productPrice = parseFloat(productData.price);
              }
            }
            
            // Method 3: Try to get from displayed price in DOM (for quickview price updates)
            if (!productPrice || productPrice === 0) {
              const quickviewContainerForPrice = form.closest('[id^="product-quickview-"]');
              if (quickviewContainerForPrice) {
                const priceElement = quickviewContainerForPrice.querySelector('.product-item-caption-price-current span.money');
                if (priceElement) {
                  const priceText = priceElement.textContent || priceElement.innerText;
                  const priceMatch = priceText.match(/[\d,]+\.?\d*/);
                  if (priceMatch) {
                    productPrice = parseFloat(priceMatch[0].replace(/,/g, ''));
                  }
                }
              }
            }
            
            // Method 4: Fallback to getProductPrice method
            if (!productPrice || productPrice === 0) {
              productPrice = pluginInstance.getProductPrice(form);
            }
            
            // Ensure price is in rupees (not cents) - if price seems too high, it might be in cents
            // Shopify prices are typically in cents, so if price > 10000, likely in cents
            if (productPrice > 10000) {
              productPrice = productPrice / 100;
            }
            
            return productPrice;
          };
          
          // Function to validate minimum order value (Buy It Now only checks order value, not quantity)
          const validateOrderValue = () => {
            const qty = parseInt(quantityInput.value) || 1;
            
            // Only check minimum order value for Buy It Now button
            const productPrice = getCurrentProductPrice();
            const orderValue = qty * productPrice;
            const minOrderValue = 5000;

            if (orderValue < minOrderValue) {
              return {
                isValid: false,
                message: 'Minimum order value is ₹5,000 to proceed to checkout'
              };
            }
            return { isValid: true };
          };

          // Intercept clicks on payment button
          paymentButton.addEventListener('click', (e) => {
            const validation = validateOrderValue();
            
            if (!validation.isValid) {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              pluginInstance.showAlert(validation.message, 'error');
              quantityInput.focus();
              quantityInput.select();
              return false;
            }
          }, true); // Use capture phase to intercept early

          // Also intercept form submission triggered by payment button
          form.addEventListener('submit', (e) => {
            // Check if this submit was triggered by payment button
            const activeElement = document.activeElement;
            const isPaymentButton = paymentButtonContainer.contains(activeElement) || 
                                   activeElement === paymentButton ||
                                   paymentButton.contains(activeElement);
            
            if (isPaymentButton) {
              const validation = validateOrderValue();
              
              if (!validation.isValid) {
                e.preventDefault();
                e.stopPropagation();
                pluginInstance.showAlert(validation.message, 'error');
                quantityInput.focus();
                quantityInput.select();
                return false;
              }
            }
          }, true);
        }
      };

      // Wait for payment button to be loaded (Shopify loads it asynchronously)
      // Start immediately and also retry after delays to catch async loading
      attachPaymentButtonHandlers(); // Try immediately
      
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          setTimeout(attachPaymentButtonHandlers, 500);
          setTimeout(attachPaymentButtonHandlers, 1500);
        });
      } else {
        setTimeout(attachPaymentButtonHandlers, 500);
        setTimeout(attachPaymentButtonHandlers, 1500);
        setTimeout(attachPaymentButtonHandlers, 3000);
      }

      // Also listen for DOM mutations in case payment button is added dynamically
      if (window.MutationObserver) {
        const observer = new MutationObserver(() => {
          const paymentButtonContainer = form.querySelector('.shopify-payment-button');
          if (paymentButtonContainer && paymentButtonContainer.dataset.minOrderValueHandlerAdded !== 'true') {
            attachPaymentButtonHandlers();
          }
        });

        observer.observe(form, {
          childList: true,
          subtree: true
        });
      }
    }

    /**
     * Get product data from page (metafields injected via Liquid)
     */
    getProductData(context) {
      const scope = context || document;

      // Prefer scoped data for modals to avoid conflicts with theme quickview JSON
      const scriptTag =
        scope.querySelector('script[type="application/json"][data-min-order-product]') ||
        scope.querySelector('script[type="application/json"][data-product]');
      if (scriptTag) {
        try {
          return JSON.parse(scriptTag.textContent);
        } catch (e) {
          console.error('Error parsing product data:', e);
        }
      }

      // Try to get from meta tags (only if no scope or scope is document)
      if (scope === document) {
        const enableMinQtyMeta = document.querySelector('meta[name="enable-min-quantity"]');
        const minQtyMeta = document.querySelector('meta[name="min-quantity"]');

        if (enableMinQtyMeta) {
          return {
            enableMinQuantity: enableMinQtyMeta.content === 'true',
            minQuantity: minQtyMeta ? parseInt(minQtyMeta.content) : 1,
            price: this.getProductPrice(scope)
          };
        }
      }

      // Try to get from data attributes on product form
      const productForm = scope.querySelector('form[action*="/cart/add"]');
      if (productForm) {
        return {
          enableMinQuantity: productForm.dataset.enableMinQuantity === 'true',
          minQuantity: parseInt(productForm.dataset.minQuantity) || 1,
          price: this.getProductPrice(scope)
        };
      }

      return null;
    }

    /**
     * Get product price from page
     */
    getProductPrice(scope) {
      const searchScope = scope || document;
      
      // Try various selectors for price
      const priceSelectors = [
        '#ProductPrice',
        '.product__price',
        '[data-product-price]',
        '.price',
        '.product-price',
        '.product-item-caption-price-current .money'
      ];

      for (const selector of priceSelectors) {
        const priceEl = searchScope.querySelector(selector);
        if (priceEl) {
          const priceText = priceEl.textContent || priceEl.innerText;
          const priceMatch = priceText.match(/[\d,]+\.?\d*/);
          if (priceMatch) {
            return parseFloat(priceMatch[0].replace(/,/g, ''));
          }
        }
      }

      // Try to get from data attribute
      const priceData = searchScope.querySelector('[data-product-price]');
      if (priceData && priceData.dataset.productPrice) {
        return parseFloat(priceData.dataset.productPrice) / 100; // Shopify prices in cents
      }

      return 0;
    }

    /**
     * Validate product page before adding to cart
     */
    validateProductPage(productData, quantityInput, event) {
      const qty = parseInt(quantityInput.value) || 1;

      // Validate minimum quantity
      if (productData.enableMinQuantity && qty < productData.minQuantity) {
        event.preventDefault();
        this.showAlert(
          `A minimum order quantity of ${productData.minQuantity} units is required for this item.`,
          'error'
        );
        quantityInput.focus();
        quantityInput.select();
        return false;
      }


      return true;
    }

    /**
     * Initialize cart page functionality
     */
    initCartPage() {
      // Get cart data (injected via Liquid)
      const cartData = this.getCartData();

      if (!cartData || !cartData.items || cartData.items.length === 0) {
        return;
      }

      // Find all checkout buttons (main cart and modal cart) with retry mechanism
      const findAndValidateButtons = () => {
        const checkoutButtons = document.querySelectorAll('button[name="checkout"], .cart-checkout-btn, .checkout-btn, a[href*="/checkout"], .cart__checkout, [name="checkout"]');

        if (checkoutButtons.length === 0) {
          // Retry after a short delay if buttons not found yet
          setTimeout(() => {
            const retryButtons = document.querySelectorAll('button[name="checkout"], .cart-checkout-btn, .checkout-btn, a[href*="/checkout"], .cart__checkout, [name="checkout"]');
            if (retryButtons.length > 0) {
              retryButtons.forEach(button => {
                this.validateCartPage(cartData, button);
              });
            }
          }, 100);
          return;
        }

        // Validate on page load for all buttons
        checkoutButtons.forEach(button => {
          this.validateCartPage(cartData, button);
        });
      };

      findAndValidateButtons();

      // Prevent form submission when minimum order value not met
      const cartForms = document.querySelectorAll('form[action*="/cart"]');
      cartForms.forEach(form => {
        if (!form.dataset.minOrderSubmitHandlerAdded) {
          form.dataset.minOrderSubmitHandlerAdded = 'true';
          form.addEventListener('submit', (e) => {
            // Get fresh cart data
            const cartData = this.getCartData();
            if (cartData) {
              const fixedMinOrderValue = 5000;
              const cartTotal = parseFloat(cartData.cartTotal || 0) / 100; // Convert from cents to rupees
              
              
              if (cartTotal < fixedMinOrderValue) {
                e.preventDefault();
                e.stopPropagation();
                alert(`The minimum order value is ₹5,000. Kindly add more items to proceed`);
                return false;
              }
            }
          });
        }
      });

      // Validate on quantity changes
      const quantityInputs = document.querySelectorAll('.cart__quantity input, .cart-item__quantity input, input[name="updates[]"]');
      quantityInputs.forEach(input => {
        input.addEventListener('change', () => {
          // Reload cart data if using AJAX cart
          if (window.fetch && window.Shopify) {
            this.reloadCartData().then(updatedCartData => {
              // Re-find all checkout buttons and validate each
              const updatedButtons = document.querySelectorAll('button[name="checkout"], .cart-checkout-btn, .checkout-btn, a[href*="/checkout"], .cart__checkout, [name="checkout"]');
              updatedButtons.forEach(button => {
                this.validateCartPage(updatedCartData, button);
              });
            });
          } else {
            // Fallback: reload page or re-validate with current data
            setTimeout(() => {
              const updatedCartData = this.getCartData();
              if (updatedCartData) {
                const updatedButtons = document.querySelectorAll('button[name="checkout"], .cart-checkout-btn, .checkout-btn, a[href*="/checkout"], .cart__checkout, [name="checkout"]');
                updatedButtons.forEach(button => {
                  this.validateCartPage(updatedCartData, button);
                });
              }
            }, 500);
          }
        });
      });

      // Validate on cart updates (if using AJAX)
      if (window.Shopify && window.Shopify.theme) {
        document.addEventListener('cart:updated', () => {
          this.reloadCartData().then(updatedCartData => {
            // Re-find all checkout buttons and validate each
            const updatedButtons = document.querySelectorAll('button[name="checkout"], .cart-checkout-btn, .checkout-btn, a[href*="/checkout"], .cart__checkout, [name="checkout"]');
            updatedButtons.forEach(button => {
              this.validateCartPage(updatedCartData, button);
            });
          });
        });
      }

      // Hook into Shopify.afterCartUpdate if it exists
      if (window.Shopify && typeof window.Shopify.afterCartUpdate === 'function') {
        const originalAfterCartUpdate = window.Shopify.afterCartUpdate;
        window.Shopify.afterCartUpdate = (cart) => {
          originalAfterCartUpdate(cart);
          // Wait a bit for DOM to update, then re-validate
          setTimeout(() => {
            this.reloadCartData().then(updatedCartData => {
              // Re-find all checkout buttons and validate each
              const updatedButtons = document.querySelectorAll('button[name="checkout"], .cart-checkout-btn, .checkout-btn, a[href*="/checkout"], .cart__checkout, [name="checkout"]');
              updatedButtons.forEach(button => {
                this.validateCartPage(updatedCartData, button);
              });
            });
          }, 500);
        };
      }

      // Also listen for DOM mutations in case cart is updated via AJAX load
      if (window.MutationObserver) {
        const observer = new MutationObserver(() => {
          const cartData = this.getCartData();
          if (cartData && cartData.items && cartData.items.length > 0) {
            const buttons = document.querySelectorAll('button[name="checkout"], .cart-checkout-btn, .checkout-btn, a[href*="/checkout"], .cart__checkout, [name="checkout"]');
            if (buttons.length > 0) {
              buttons.forEach(button => {
                this.validateCartPage(cartData, button);
              });
            }
          }
        });

        // Observe cart form changes
        cartForms.forEach(form => {
          observer.observe(form, {
            childList: true,
            subtree: true
          });
        });
      }
    }

    /**
     * Get cart data from page (injected via Liquid)
     */
    getCartData() {
      // Try to get from script tag
      const scriptTag = document.querySelector('script[type="application/json"][data-cart]');
      if (scriptTag) {
        try {
          return JSON.parse(scriptTag.textContent);
        } catch (e) {
          console.error('Error parsing cart data:', e);
        }
      }

      // Try to get from window object (if Liquid injected)
      if (window.cartData) {
        return window.cartData;
      }

      // Try to get from data attribute
      const cartElement = document.querySelector('[data-cart]');
      if (cartElement && cartElement.dataset.cart) {
        try {
          return JSON.parse(cartElement.dataset.cart);
        } catch (e) {
          console.error('Error parsing cart data from attribute:', e);
        }
      }

      return null;
    }

    /**
     * Reload cart data via AJAX
     */
    async reloadCartData() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        
        // Get global min cart total from existing cart data
        const existingCartData = this.getCartData();
        const globalMinCartTotal = existingCartData?.globalMinCartTotal || 0;
        const enableGlobalMinCart = existingCartData?.enableGlobalMinCart || false;
        
        // Transform cart items to include metafields
        // Preserve existing metafields from page data since cart.js doesn't include them
        const itemsWithMetafields = cart.items.map(item => {
          const existingItem = existingCartData?.items?.find(existing => 
            existing.variantId === item.variant_id || existing.id === item.id
          );
          
          return {
            id: item.id,
            quantity: item.quantity,
            price: item.price,
            productId: item.product_id,
            variantId: item.variant_id,
            productTitle: item.product_title,
            title: item.title,
            product: {
              id: item.product_id,
              title: item.product_title,
              metafields: existingItem?.product?.metafields || {}
            }
          };
        });
        
        return {
          globalMinCartTotal: globalMinCartTotal,
          enableGlobalMinCart: enableGlobalMinCart,
          cartTotal: cart.total_price,
          items: itemsWithMetafields
        };
      } catch (error) {
        console.error('Error reloading cart data:', error);
        return this.getCartData();
      }
    }

    /**
     * Validate cart page - prevent checkout with alert if minimum not met
     */
    validateCartPage(cartData, checkoutButton) {
      if (!checkoutButton) {
        return true;
      }

      // Add click handler to checkout button to show alert and prevent submission
      if (!checkoutButton.dataset.minOrderHandlerAdded) {
        checkoutButton.dataset.minOrderHandlerAdded = 'true';
        
        // Handle button click - get fresh cart data
        checkoutButton.addEventListener('click', (e) => {
          // Get fresh cart data on click
          const freshCartData = this.getCartData() || cartData;
          if (freshCartData) {
            const fixedMinOrderValue = 5000;
            const cartTotal = parseFloat(freshCartData.cartTotal || 0) / 100; // Convert from cents to rupees
            
            if (cartTotal < fixedMinOrderValue) {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              alert(`The minimum order value is ₹5,000. Kindly add more items to proceed`);
              return false;
            }
          }
        }, true); // Use capture phase to intercept early
      }

      return true;
    }

    /**
     * Show cart validation errors
     */
    showCartErrors(errors, canCheckout) {
      // Remove existing error messages
      const existingErrors = document.querySelectorAll('.min-order-value-error');
      existingErrors.forEach(el => el.remove());

      if (errors.length === 0 && canCheckout) {
        return;
      }

      // Create error container
      const errorContainer = document.createElement('div');
      errorContainer.className = 'min-order-value-error';
      errorContainer.style.cssText = `
        background-color: #fff3cd;
        border: 1px solid #ffc107;
        color: #856404;
        padding: 15px;
        margin: 15px 0;
        border-radius: 4px;
      `;

      if (!canCheckout) {
        const errorTitle = document.createElement('strong');
        errorTitle.textContent = '⚠️ Cannot proceed to checkout:';
        errorTitle.style.display = 'block';
        errorTitle.style.marginBottom = '10px';
        errorContainer.appendChild(errorTitle);

        const errorList = document.createElement('ul');
        errorList.style.margin = '0';
        errorList.style.paddingLeft = '20px';
        errors.forEach(error => {
          const li = document.createElement('li');
          li.textContent = error;
          errorList.appendChild(li);
        });
        errorContainer.appendChild(errorList);
      }

      // Insert before checkout button or cart summary
      const checkoutButton = document.querySelector('button[name="checkout"]') ||
                            document.querySelector('a[href*="/checkout"]') ||
                            document.querySelector('.cart__checkout');
      
      if (checkoutButton) {
        checkoutButton.parentNode.insertBefore(errorContainer, checkoutButton);
      } else {
        // Fallback: append to cart form or body
        const cartForm = document.querySelector('form[action*="/cart"]');
        if (cartForm) {
          cartForm.insertBefore(errorContainer, cartForm.firstChild);
        }
      }
    }

    /**
     * Show alert message
     */
    showAlert(message, type = 'info') {
      // Try to use theme's notification system if available
      if (window.theme && window.theme.notify) {
        window.theme.notify(message, type);
        return;
      }

      // Fallback to browser alert
      alert(message);
    }
  }

  // Initialize plugin when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.__MinQuantityOrderValuePluginInstance = new MinQuantityOrderValuePlugin();
      // Mark validation as ready after initialization
      setTimeout(() => {
        window.__MinQuantityValidationReady = true;
      }, 500);
    });
  } else {
    window.__MinQuantityOrderValuePluginInstance = new MinQuantityOrderValuePlugin();
    // Mark validation as ready after initialization
    setTimeout(() => {
      window.__MinQuantityValidationReady = true;
    }, 500);
  }

  // Re-initialize on AJAX page loads (for themes with AJAX navigation)
  if (window.Shopify && window.Shopify.theme) {
    document.addEventListener('shopify:section:load', () => {
      window.__MinQuantityOrderValuePluginInstance = new MinQuantityOrderValuePlugin();
    });
  }

  // Function to initialize quickview modals
  const initQuickviewModals = () => {
    const mfpContent = document.querySelector('.mfp-content');
    if (mfpContent) {
      const quickviewContainers = mfpContent.querySelectorAll('[id^="product-quickview-"]');
      if (quickviewContainers.length > 0) {
        // Use existing plugin instance or create new one
        const plugin = window.__MinQuantityOrderValuePluginInstance || new MinQuantityOrderValuePlugin();
        if (!window.__MinQuantityOrderValuePluginInstance) {
          window.__MinQuantityOrderValuePluginInstance = plugin;
        }
        quickviewContainers.forEach(container => {
          plugin.initQuickviewModal(container);
        });
      }
    }
  };

  // Initialize quickview modals when Magnific Popup opens
  if (window.jQuery && window.jQuery(document) && window.jQuery(document).on) {
    // Listen for mfpOpen event
    window.jQuery(document).on('mfpOpen', () => {
      // defer to allow modal DOM to fully mount
      setTimeout(initQuickviewModals, 400);
    });

    // Also listen for AJAX content added (when modal content is loaded)
    window.jQuery(document).on('mfpAjaxContentAdded', () => {
      setTimeout(initQuickviewModals, 400);
    });
  }

  // Fallback: If script loads after modal is already open, initialize immediately
  // This handles the case when script is loaded inside the modal template
  setTimeout(() => {
    const mfpWrap = document.querySelector('.mfp-wrap.mfp-ready');
    if (mfpWrap) {
      // Modal is already open, initialize
      setTimeout(initQuickviewModals, 200);
    }
  }, 100);

})();